import {ConfigProvider} from 'antd'
import {docs_getCodiDocumentsRowDetails_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import {UseHook} from 'components/UseHook'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useContext, useEffect, useRef, useState} from 'react'
import {BsChat, BsGearFill,} from 'react-icons/bs'
import {IoNewspaper} from 'react-icons/io5'
import {Switch} from 'react-router-dom'
import RouteWithSubRoutes from 'routes/RouteWithSubRoutes'
import LoadingPage from 'views/LoadingPage'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {CodiRowActions} from '..'
import CodiDocumentContext, {CodiDocumentProvider,} from '../CodiDocumentContext'
import {CodiDocumentRowActions} from '../components/Codi/CodiDocumentActions'
import {CodiDocumentTree} from '../components/Codi/CodiDocumentMenu'
import {CodiSiderHeading} from '../components/Codi/CodiSiderHeading'
import CodiRowAmendment from './CodiRowAmendment'
import CodiRowComments from './CodiRowComments'
import CodiRowInstruction from './CodiRowInstruction'
import {RowLinkedArticles, RowLinkedCodiDocuments, RowLinkedDocuments,} from './CodiRowLinkedList'
import CodiRowReplacement from './CodiRowReplacement'
import {LawSvg} from "../../../components/Svgs/LawSvg";
import {DocSettingSvg} from "../../../components/Svgs/DocSettingSvg";
import {DocReplacementSvg} from "../../../components/Svgs/DocReplacementSvg";
import {DocInstructionSvg} from "../../../components/Svgs/DocInstructionSvg";
import {PiBooks} from "react-icons/pi";
import {VscListTree} from "react-icons/vsc";
import {copyLocationParam, getRouteSearchParams, useRouteSearchParam} from "../../../envs/_current/history";

const CodiRowContext = React.createContext({})

export const CodiRowIndexes = (props) => {
  const history = useHistory()

  const id = useRouteSearchParam('id')

  let searchParams = getRouteSearchParams()

  return (
    <CodiDocumentContext.Consumer>
      {({item, isLoading}) => {
        if (isLoading) {
          return <LoadingPage/>
        }
        return (
          <CodiDocumentTree
            item={item}
            className="p-2"
            defaultExpandedKeys={[id]}
            onItemClick={(info, {item}) => {
              if (item?.id) {
                searchParams.set('id', item?.id)
                history.push({
                  ...copyLocationParam(window.location ?? {}),
                  search: '?' + searchParams.toString(),
                })
              }
            }}
            header={
              <CodiSiderHeading
                title="table of contents"
                icon={
                  <VscListTree
                    size={20}
                    className="text-primary"
                  />
                }
              />
            }
            {...props}
          />
        )
      }}
    </CodiDocumentContext.Consumer>
  )
}

const ModalLayout = ({title, children, ...props}) => {
  const t = useTranslate()
  return (
    <FullSideModalLayout
      center={true}
      title={
        _.isString(title) ? (
          <span className="max-lines-2 font-medium text-sm text-color-000">
            {t(title)}
          </span>
        ) : (
          title
        )
      }
      {...props}>
      {children}
    </FullSideModalLayout>
  )
}

const withRouteModal =
  (params) =>
    (Component = NullAsDefault, props) =>
      (
        <CodiRowContext.Consumer>
          {({item, setCurrent = NullAsDefault}) => {
            return (
              <ModalLayout
                {...(params ?? {})}
                onCancel={() => {
                  setCurrent(null)
                }}>
                <Component {...(props ?? {})} />
              </ModalLayout>
            )
          }}
        </CodiRowContext.Consumer>
      )

const routes = {
  indexes: {
    path: 'indexes',
    name: 'indexes',
    component: () =>
      withRouteModal({
        footer: null,
        title: null,
      })(CodiRowIndexes),
    exact: true,
  },
  comments: {
    path: 'comments',
    name: 'comments',
    component: () => (
      <CodiRowContext>
        {({item}) =>
          withRouteModal({
            footer: null,
            title: null,
          })(CodiRowComments, {
            className: 'p-2',
            id: getId(item),
            type: getType(item),
            header: (
              <CodiSiderHeading
                title="comments"
                icon={
                  <BsChat
                    size={20}
                    className="text-primary"
                  />
                }
              />
            ),
          })
        }
      </CodiRowContext>
    ),
    exact: true,
  },
  instruction: {
    path: 'instruction',
    name: 'instruction',
    component: () => (
      <CodiRowContext>
        {({item}) =>
          withRouteModal({
            footer: null,
            title: null,
          })(CodiRowInstruction, {
            className: 'p-2',
            id: getId(item),
            type: getType(item),
            header: (
              <CodiSiderHeading
                title="instruction"
                icon={
                  <DocInstructionSvg
                    size={20} width={20} height={20}
                    className="text-primary"
                  />
                }
              />
            ),
          })
        }
      </CodiRowContext>
    ),
    exact: true,
  },
  replacement: {
    path: 'replacement',
    name: 'replacement',
    component: () => (
      <CodiRowContext>
        {({item}) =>
          withRouteModal({
            footer: null,
            title: null,
          })(CodiRowReplacement, {
            className: 'p-2',
            id: getId(item),
            type: getType(item),
            header: (
              <CodiSiderHeading
                title="replacement"
                icon={
                  <DocReplacementSvg
                    size={20} width={20} height={20}
                    className="text-primary"
                  />
                }
              />
            ),
          })
        }
      </CodiRowContext>
    ),
    exact: true,
  },
  amendment: {
    path: 'amendment',
    name: 'amendment',
    component: () => (
      <CodiRowContext>
        {({item}) =>
          withRouteModal({
            footer: null,
            title: null,
          })(CodiRowAmendment, {
            item,
            className: 'p-2',
            header: (
              <CodiSiderHeading
                title="amendment"
                icon={
                  <DocSettingSvg
                    size={20} width={20} height={20}
                    className="text-primary"
                  />
                }
              />
            ),
          })
        }
      </CodiRowContext>
    ),
    exact: true,
  },
  linkedDocuments: {
    path: 'linked-documents',
    name: 'linkedDocuments',
    component: () =>
      withRouteModal({
        footer: null,
        title: 'associated link documents',
      })(RowLinkedDocuments, {
        className: 'p-2',
        header: (
          <CodiSiderHeading
            title="associated link documents"
            icon={
              <LawSvg size={20} width={20} height={20}
                      className="text-primary"
              />
            }
          />
        ),
      }),
    exact: true,
  },
  linkedCodiDocuments: {
    path: 'linked-codi-documents',
    name: 'linkedCodiDocuments',
    component: () =>
      withRouteModal({
        footer: null,
        title: 'codi link documents',
      })(RowLinkedCodiDocuments, {
        className: 'p-2',
        header: (
          <CodiSiderHeading
            title="codi link documents"
            icon={
              <PiBooks
                size={20}
                width={20}
                height={20}
                className="text-primary"
              />
            }
          />
        ),
      }),
    exact: true,
  },
  linkedArticles: {
    path: 'linked-articles',
    name: 'linkedArticles',
    component: () =>
      withRouteModal({
        footer: null,
        title: 'linked articles',
      })(RowLinkedArticles, {
        className: 'p-2',
        header: (
          <CodiSiderHeading
            title="linked articles"
            icon={
              <IoNewspaper
                size={20} width={20} height={20}
                className="text-primary"
              />
            }
          />
        ),
      }),
    exact: true,
  },
  settings: {
    path: 'settings',
    name: 'settings',
    component: () =>
      withRouteModal({
        footer: null,
        title: null,
      })(CodiDocumentRowActions, {
        className: 'p-2',
        header: (
          <CodiSiderHeading
            title="management"
            icon={
              <BsGearFill
                size={20}
                className="text-primary"
              />
            }
          />
        ),
      }),
    exact: true,
  },
}

const Provider = ({children, initialCurrent, codi_document_id}) => {
  const modalDismissRef = useRef(NullAsDefault)

  const {item: codi_document} = useContext(CodiDocumentContext)

  const [current, setCurrent] = useState(initialCurrent)

  const [refreshKey, setRefreshKey] = useState()

  const id = useRouteSearchParam('id')

  const {
    response,
    status: _ignored,
    isLoading = false,
    ...rest
  } = useAsyncAction({
    apiInfo: docs_getCodiDocumentsRowDetails_Api,
  })

  useEffect(() => {
    if (id) {
      if (_.isFunction(rest?.handleAsyncAction)) {
        rest.handleAsyncAction({}, {id})
      }
    }
  }, [id])

  const item = getResponseItem(response)

  const error = getResponseItem(response, 'error')

  const errorMessage = _.get(response, 'errorMessages.error')

  const error_code = _.get(response, 'errorMessages.error_code')

  const status = {
    title: response?.status,
    description: errorMessage,
  }

  const isSucceed = Number(_.get(response, 'status')) === 200

  const handleAction = (action, params) => {
    switch (action) {
      case CodiRowActions.SHOW_COMMENTS_MODAL:
        setCurrent(routes.comments.path)
        break
      case CodiRowActions.SHOW_SETTINGS_MODAL:
        setCurrent(routes.settings.path)
        break
      case CodiRowActions.SHOW_INDEXES_MODAL:
        setCurrent(routes.indexes.path)
        break
      case CodiRowActions.SHOW_INSTRUCTION_MODAL:
        setCurrent(routes.instruction.path)
        break
      case CodiRowActions.SHOW_REPLACEMENT_MODAL:
        setCurrent(routes.replacement.path)
        break
      case CodiRowActions.SHOW_AMENDMENT_MODAL:
        setCurrent(routes.amendment.path)
        break
      case CodiRowActions.SHOW_LINKED_DOCUMENTS_MODAL:
        setCurrent(routes.linkedDocuments.path)
        break
      case CodiRowActions.SHOW_LINKED_ARTICLES_MODAL:
        setCurrent(routes.linkedArticles.path)
        break
      case CodiRowActions.SHOW_LINKED_CODI_DOCUMENTS_MODAL:
        setCurrent(routes.linkedCodiDocuments.path)
        break
      default:
        break
    }
  }

  return (
    <CodiRowContext.Provider
      key={id}
      value={{
        id,
        item,
        error,
        status,
        current,
        isLoading,
        isSucceed,
        error_code,
        refreshKey,
        setCurrent,
        errorMessage,
        handleAction,
        setRefreshKey,
        codi_document,
        modalDismissRef,
        codi_document_id,
        ...rest,
      }}>
      <PermissionProvider>
        <React.Fragment>
          {children}
          {current && (
            <Switch
              location={{
                hash: '',
                search: '',
                key: current,
                pathname: current,
              }}>
              {Object.values(routes).map((route, index) => (
                <RouteWithSubRoutes
                  key={index}
                  {...route}
                />
              ))}
            </Switch>
          )}
        </React.Fragment>
      </PermissionProvider>
    </CodiRowContext.Provider>
  )
}

export const CodiRowProvider = ({codi_document_id, ...props}) => (
  <UseHook
    hook={useRouteSearchParam}
    deps={['initialCurrent']}>
    {(initialCurrent) => (
      <ConfigProvider renderEmpty={EmptyHolder}>
        <CodiDocumentProvider id={codi_document_id}>
          <Provider
            {...{
              initialCurrent,
              codi_document_id,
            }}
            {...props}
          />
        </CodiDocumentProvider>
      </ConfigProvider>
    )}
  </UseHook>
)

export default CodiRowContext
